import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useUnifiedUsers } from "@/hooks/useUnifiedUsers";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { UserTypeBadge } from "@/components/users/UserTypeBadge";
import { ActionsDropdown } from "@/components/ui/actions-dropdown";
import { UserDetailsTab } from "@/components/users/detail/UserDetailsTab";
import { UserDevicesTab } from "@/components/users/detail/UserDevicesTab";
import { UserGroupsTab } from "@/components/users/detail/UserGroupsTab";
import { UserActivityTab } from "@/components/users/detail/UserActivityTab";
import { UserAuditTab } from "@/components/users/detail/UserAuditTab";
import { ArrowLeft, Edit, Trash2, Shield, User, Monitor, Users, Activity, FileText } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

export default function UnifiedUserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { users, isLoading } = useUnifiedUsers();
  const [activeTab, setActiveTab] = useState("details");

  if (isLoading) {
    return (
      <div className="h-full flex flex-col bg-background">
        <div className="animate-pulse space-y-6 p-6">
          <div className="h-8 bg-muted rounded w-1/3"></div>
          <div className="h-32 bg-muted rounded"></div>
          <div className="h-64 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  const user = users.find(u => u.id === userId || u.directoryId === userId || u.consoleId === userId);

  if (!user) {
    return (
      <div className="h-full flex flex-col bg-background p-6">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-2xl font-bold mb-4">User Not Found</h1>
          <p className="text-muted-foreground mb-6">
            The user you're looking for doesn't exist or you don't have permission to view it.
          </p>
          <Button onClick={() => navigate("/admin/users-and-roles")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Users
          </Button>
        </div>
      </div>
    );
  }

  const groupCount = user.groups?.length || 0;

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="border-b bg-card/30">
        {/* Back navigation and Actions */}
        <div className="flex items-center justify-between px-6 py-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/admin/users-and-roles")}
            className="relative overflow-hidden group transition-all duration-300
              before:content-[''] before:absolute before:inset-0 before:rounded-md
              before:bg-gradient-radial before:from-white/30 before:via-white/20 before:to-white/10
              before:dark:from-white/20 before:dark:via-white/10 before:dark:to-transparent
              before:opacity-0 before:backdrop-blur-xl before:transition-all before:duration-300
              before:scale-95 before:translate-y-1
              hover:before:opacity-100 hover:before:scale-100 hover:before:translate-y-0
              hover:shadow-lg hover:shadow-white/10 dark:hover:shadow-black/20
              after:content-[''] after:absolute after:inset-0 after:rounded-md
              after:border after:border-white/30 after:dark:border-white/20
              after:opacity-0 after:transition-opacity after:duration-300
              hover:after:opacity-100"
          >
            <ArrowLeft className="h-4 w-4 mr-2 relative z-10" />
            <span className="relative z-10">Back to Users</span>
          </Button>

          <ActionsDropdown
            align="end"
            actions={[
              {
                label: "Edit User",
                icon: Edit,
                onClick: () => {},
              },
              ...(user.type !== 'directory' ? [{
                label: "Manage Roles",
                icon: Shield,
                onClick: () => {},
              }] : []),
              {
                label: "Delete User",
                icon: Trash2,
                onClick: () => {},
                destructive: true,
              },
            ]}
          />
        </div>

        {/* User Info */}
        <div className="px-6 pb-8 pt-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 rounded-xl bg-muted flex items-center justify-center">
                <User className="w-8 h-8 text-muted-foreground" />
              </div>
              <div className="flex-1">
                <h1 className="text-2xl font-semibold text-foreground">{user.name}</h1>
                <p className="text-muted-foreground mt-1">{user.email}</p>
                <div className="flex items-center space-x-3 mt-3">
                  <UserTypeBadge type={user.type} />
                  <Badge variant={user.status === 'active' ? 'default' : 'secondary'}>
                    {user.status}
                  </Badge>
                  {user.roles && user.roles.length > 0 && (
                    <div className="flex gap-1">
                      {user.roles.slice(0, 2).map((userRole) => (
                        <Badge key={userRole.id} variant="outline" className="text-xs">
                          {userRole.role?.name}
                        </Badge>
                      ))}
                      {user.roles.length > 2 && (
                        <Badge variant="secondary" className="text-xs">
                          +{user.roles.length - 2}
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Resource Summary */}
            <div className="text-right">
              <div className="text-lg font-semibold text-foreground">
                {user.deviceCount + groupCount}
              </div>
              <div className="text-xs text-muted-foreground">Total Resources</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Section */}
      <div className="flex-1 overflow-auto">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="px-6 py-4 bg-card/30">
            <TabsList>
              <TabsTrigger value="details" className="hover:bg-transparent hover:text-current">
                <span className="font-medium">Details</span>
              </TabsTrigger>
              <TabsTrigger value="devices" className="hover:bg-transparent hover:text-current">
                <span className="font-medium">Devices</span>
                <span className="ml-2 text-xs bg-muted px-1.5 py-0.5 rounded-full">
                  {user.deviceCount}
                </span>
              </TabsTrigger>
              {(user.type === 'directory' || user.type === 'both') && groupCount > 0 && (
                <TabsTrigger value="groups" className="hover:bg-transparent hover:text-current">
                  <span className="font-medium">Groups</span>
                  <span className="ml-2 text-xs bg-muted px-1.5 py-0.5 rounded-full">
                    {groupCount}
                  </span>
                </TabsTrigger>
              )}
              <TabsTrigger value="activity" className="hover:bg-transparent hover:text-current">
                <span className="font-medium">Activity</span>
              </TabsTrigger>
              <TabsTrigger value="audit" className="hover:bg-transparent hover:text-current">
                <span className="font-medium">Audit Log</span>
              </TabsTrigger>
            </TabsList>
          </div>

          <div className="p-6">
            <TabsContent value="details" className="mt-0">
              <UserDetailsTab user={user} />
            </TabsContent>

            <TabsContent value="devices" className="mt-0">
              <UserDevicesTab user={user} />
            </TabsContent>

            {(user.type === 'directory' || user.type === 'both') && (
              <TabsContent value="groups" className="mt-0">
                <UserGroupsTab user={user} />
              </TabsContent>
            )}

            <TabsContent value="activity" className="mt-0">
              <UserActivityTab user={user} />
            </TabsContent>

            <TabsContent value="audit" className="mt-0">
              <UserAuditTab user={user} />
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  );
}
